import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, DollarSign, Users, AlertCircle, Check, X, Pencil } from 'lucide-react';
import billToBillService from '../services/billToBillService';
import { showSuccess, showError } from '../services/toastService';
import './BillToBillManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';

const BillToBillManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
    const [selectedUser, setSelectedUser] = useState(null);
    const [outstandingModalOpen, setOutstandingModalOpen] = useState(false);
    const [outstandingAmount, setOutstandingAmount] = useState('');

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (searchTerm) next.set('search', searchTerm); else next.delete('search');
            next.delete('page');
            return next;
        }, { replace: true });
    }, [searchTerm, setSearchParams]);

    useEffect(() => {
        fetchUsers();
        fetchStats();
    }, [pagination.page, searchTerm]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm
            };
            const response = await billToBillService.getAllUsers(params);
            if (response.success) {
                setUsers((current) => pagination.page === 1 ? response.data.users : [...current, ...response.data.users]);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            showError(error.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await billToBillService.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleToggleBillToBill = async (user) => {
        const newStatus = !user.is_bill_to_bill;

        try {
            const response = await billToBillService.toggleBillToBillStatus(user.id, {
                is_bill_to_bill: newStatus,
                outstanding_amount: newStatus ? user.outstanding_amount : 0
            });

            if (response.success) {
                showSuccess(`${user.business_name} ${newStatus ? 'added to' : 'removed from'} bill-to-bill`);
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            showError(error.message || 'Failed to update bill-to-bill status');
        }
    };

    const handleUpdateOutstanding = (user) => {
        setSelectedUser(user);
        setOutstandingAmount(user.outstanding_amount.toString());
        setOutstandingModalOpen(true);
    };

    const handleOutstandingSubmit = async () => {
        if (!selectedUser) return;

        const amount = parseFloat(outstandingAmount);
        if (isNaN(amount) || amount < 0) {
            showError('Please enter a valid amount');
            return;
        }

        try {
            const response = await billToBillService.updateOutstandingAmount(selectedUser.id, amount);
            if (response.success) {
                showSuccess('Outstanding amount updated successfully');
                setOutstandingModalOpen(false);
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            showError(error.message || 'Failed to update outstanding amount');
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const getApprovalBadge = (status) => {
        const variants = {
            approved: 'active',
            pending: 'pending',
            rejected: 'destructive'
        };
        return (
            <Badge variant={variants[status] || 'inactive'}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };

    return (
        <div className="bill-to-bill-container">
            {/* Header */}
            <div className="btb-header">
                <div>
                    <h1 className="btb-title">Bill to Bill Management</h1>
                    <p className="btb-subtitle">Manage users who must pay previous bills before new orders</p>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="btb-stats-grid">
                <Card>
                    <CardContent className="btb-stat-card">
                        <div className="btb-stat-icon">
                            <Users size={24} />
                        </div>
                        <div className="btb-stat-content">
                            <p className="btb-stat-label">Total Users</p>
                            <p className="btb-stat-value">{stats.total_users || 0}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="btb-stat-card">
                        <div className="btb-stat-icon btb-stat-icon-warning">
                            <CreditCard size={24} />
                        </div>
                        <div className="btb-stat-content">
                            <p className="btb-stat-label">Bill-to-Bill Users</p>
                            <p className="btb-stat-value">{stats.bill_to_bill_users || 0}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="btb-stat-card">
                        <div className="btb-stat-icon btb-stat-icon-danger">
                            <AlertCircle size={24} />
                        </div>
                        <div className="btb-stat-content">
                            <p className="btb-stat-label">With Outstanding</p>
                            <p className="btb-stat-value">{stats.users_with_outstanding || 0}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="btb-stat-card">
                        <div className="btb-stat-icon btb-stat-icon-success">
                            <DollarSign size={24} />
                        </div>
                        <div className="btb-stat-content">
                            <p className="btb-stat-label">Total Outstanding</p>
                            <p className="btb-stat-value">{formatCurrency(stats.total_outstanding_amount || 0)}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Users List */}
            <Card>
                <CardHeader>
                    <CardTitle>Users List</CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Filters */}
                    <div className="btb-filters">
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search by business name, email, or contact person..."
                            className="btb-search-input"
                        />
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Business Name</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Approval Status</TableHead>
                                <TableHead>Bill-to-Bill</TableHead>
                                <TableHead>Outstanding Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="5" className="btb-table-empty">Loading...</TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="5" className="btb-table-empty">No users found</TableCell>
                                </TableRow>
                            ) : (
                                users.map(user => (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <div>
                                                <div className="btb-business-name">{user.business_name}</div>
                                                <div className="btb-email">{user.email}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <div className="btb-contact-person">{user.contact_person}</div>
                                                <div className="btb-phone">{user.phone}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {getApprovalBadge(user.approval_status)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="btb-toggle-container">
                                                <button
                                                    onClick={() => handleToggleBillToBill(user)}
                                                    className={`btb-toggle ${user.is_bill_to_bill ? 'btb-toggle-on' : 'btb-toggle-off'}`}
                                                    disabled={user.approval_status !== 'approved'}
                                                    title={user.approval_status !== 'approved' ? 'User must be approved first' : ''}
                                                >
                                                    <div className="btb-toggle-slider">
                                                        {user.is_bill_to_bill ? <Check size={12} /> : <X size={12} />}
                                                    </div>
                                                </button>
                                                <span className="btb-toggle-label">
                                                    {user.is_bill_to_bill ? 'ON' : 'OFF'}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="btb-outstanding-container">
                                                <span className={`btb-outstanding-amount ${parseFloat(user.outstanding_amount) > 0 ? 'btb-outstanding-positive' : ''}`}>
                                                    {formatCurrency(user.outstanding_amount)}
                                                </span>
                                                {user.is_bill_to_bill && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleUpdateOutstanding(user)}
                                                        className="btb-edit-outstanding"
                                                        title="Update outstanding amount"
                                                    >
                                                        <Pencil size={14} />
                                                        <span className="sr-only">Edit outstanding amount</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <InfiniteScrollSentinel
                        hasMore={pagination.page < pagination.pages}
                        loading={loading}
                        onLoadMore={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                </CardContent>
            </Card>

            {/* Outstanding Amount Update Modal */}
            <Dialog open={outstandingModalOpen} onOpenChange={setOutstandingModalOpen}>
                <DialogContent className="btb-modal">
                    <DialogHeader>
                        <DialogTitle>Update Outstanding Amount</DialogTitle>
                        <DialogDescription>
                            Update the outstanding amount for <strong>{selectedUser?.business_name}</strong>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="btb-modal-content">
                        <div className="btb-form-group">
                            <label className="btb-form-label">Outstanding Amount (₹)</label>
                            <Input
                                type="number"
                                value={outstandingAmount}
                                onChange={(e) => setOutstandingAmount(e.target.value)}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                className="btb-form-input"
                            />
                            <small className="btb-form-hint">
                                Enter the total outstanding amount for this user
                            </small>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOutstandingModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleOutstandingSubmit}>
                            Update Amount
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default BillToBillManagement;